/**
 * RevenueRegisterDetailController — page mois détail
 * `/livre-des-recettes-detail.html?year=N&month=MM`
 * (EPIC-099 / 99.27 §7.H.C.2).
 *
 * Charge `GET /revenue/register/{year}-{month}` et rend :
 *  - le tableau des entrées triées chronologiquement (avec colonnes HT/TVA
 *    conditionnelles selon `meta.isVatSubject`) ;
 *  - les bandeaux contextuels (mois scellé / mois confirmé / pré-confirmation
 *    §7.I.F / intégrité hash / écritures rétroactives) — mutuellement
 *    exclusifs sauf le bandeau intégrité hash qui se cumule ;
 *  - les CTA d'action (saisir paiement, ouvrir session caisse, confirmer le
 *    mois) avec leur état activé/désactivé selon `meta.sealed`,
 *    `meta.confirmed`, `meta.confirmEligible`.
 *
 * Si l'URL contient `?confirm=1`, la modale de confirmation s'auto-ouvre une
 * fois les données chargées (deep-link partageable depuis le dashboard §15.B).
 *
 * Convention `frontend/CLAUDE.md` : aucune construction HTML inline en JS,
 * uniquement clone de `<template>` + remplissage `data-slot` + manipulation
 * DOM élément par élément.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const SOURCE_LABELS = {
  invoice: "Facture",
  standalone_named: "Détaché",
  standalone_anonymous: "Détaché",
  cash_register_named: "Caisse",
  cash_register_anonymous: "Caisse",
};

const SOURCE_PILL_MODIFIER = {
  invoice: "nm-pill--blue",
  standalone_named: "nm-pill--ochre",
  standalone_anonymous: "nm-pill--ochre",
  cash_register_named: "nm-pill--olive",
  cash_register_anonymous: "nm-pill--olive",
};

const MONTH_LABELS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export default class RevenueRegisterDetailController extends BaseController {
  static targets = [
    "title",
    "crumbMonth",
    "crumbYearLink",
    "tbody",
    "tfoot",
    "thHt",
    "thVat",
    "totalExclVat",
    "totalVat",
    "totalInclVat",
    "addPaymentButton",
    "openSessionButton",
    "confirmMonthButton",
    "sealedBanner",
    "sealedAt",
    "confirmedBanner",
    "confirmedAt",
    "preConfirmBanner",
    "preConfirmTitle",
    "preConfirmDate",
    "hashIntegrityBanner",
    "retroactiveBanner",
    "retroactiveTitle",
  ];

  connect() {
    this._fetchSeq = 0;
    const { year, month } = this._readYearMonthFromUrl();
    this._year = year;
    this._month = month;
    this._isVatSubject = false;
    this._writeStaticLabels();
    this.refresh();
  }

  refresh() {
    if (!this._year || !this._month) return;
    this._fetch();
  }

  _readYearMonthFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const today = new Date();
    let year = parseInt(params.get("year") ?? "", 10);
    let month = parseInt(params.get("month") ?? "", 10);
    if (!Number.isFinite(year) || year < 1900) year = today.getFullYear();
    if (!Number.isFinite(month) || month < 1 || month > 12) month = today.getMonth() + 1;
    return { year, month };
  }

  _writeStaticLabels() {
    const monthLabel = this._capitalize(MONTH_LABELS[this._month - 1] ?? "");
    const label = `${monthLabel} ${this._year}`.trim();
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = `Livre des recettes — ${label}`;
    }
    if (this.hasCrumbMonthTarget) {
      this.crumbMonthTarget.textContent = label;
    }
    if (this.hasCrumbYearLinkTarget) {
      this.crumbYearLinkTarget.setAttribute(
        "href",
        `/livre-des-recettes.html?year=${encodeURIComponent(this._year)}`,
      );
    }
    document.title = `Notamicro — Livre des recettes — ${label}`;

    const firstDayOfMonth = `${this._year}-${String(this._month).padStart(2, "0")}-01`;
    if (this.hasAddPaymentButtonTarget) {
      this.addPaymentButtonTarget.setAttribute(
        "href",
        `/paiements-edition.html?date=${encodeURIComponent(firstDayOfMonth)}`,
      );
    }
    if (this.hasOpenSessionButtonTarget) {
      this.openSessionButtonTarget.setAttribute(
        "href",
        `/caisse-edition.html?date=${encodeURIComponent(firstDayOfMonth)}`,
      );
    }
  }

  async _fetch() {
    const seq = ++this._fetchSeq;
    try {
      const response = await api.getRevenueRegisterMonthDetail(this._year, this._month);
      if (seq !== this._fetchSeq) return;
      const meta = response?.meta ?? {};
      this._applyVatVisibility(Boolean(meta.isVatSubject));
      this._renderEntries(Array.isArray(response?.entries) ? response.entries : []);
      this._renderTotals(response?.totals ?? {});
      this._renderBanners(meta);
      this._updateActionButtons(meta);
      this._maybeAutoOpenConfirmModal(meta);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger le détail du mois.";
      this.showError(msg);
      this._renderEntries([]);
    }
  }

  _applyVatVisibility(isVatSubject) {
    this._isVatSubject = isVatSubject;
    if (this.hasThHtTarget) this.thHtTarget.hidden = !isVatSubject;
    if (this.hasThVatTarget) this.thVatTarget.hidden = !isVatSubject;
    if (this.hasTotalExclVatTarget) this.totalExclVatTarget.hidden = !isVatSubject;
    if (this.hasTotalVatTarget) this.totalVatTarget.hidden = !isVatSubject;
  }

  _renderEntries(entries) {
    if (!this.hasTbodyTarget) return;
    this._clearChildren(this.tbodyTarget);

    if (entries.length === 0) {
      const empty = this.cloneTemplate("tpl-revenue-detail-empty");
      if (empty) {
        const isFuture = this._isFutureMonth();
        const label = isFuture
          ? "Mois à venir — aucune entrée."
          : "Aucune écriture sur ce mois.";
        this.fillSlots(empty, { emptyLabel: label });
        this.tbodyTarget.appendChild(empty);
      }
      return;
    }

    for (const entry of entries) {
      const fragment = this.cloneTemplate("tpl-revenue-detail-row");
      const sourceType = entry.sourceType ?? "";
      const sourceLabel = SOURCE_LABELS[sourceType] ?? sourceType;
      const vat = Number(entry.vatAmount ?? 0) || 0;

      this.fillSlots(fragment, {
        entryNumber: entry.entryNumber ? "#" + String(entry.entryNumber).padStart(3, "0") : "—",
        entryDateLabel: this._formatDate(entry.entryDate),
        clientLabel: entry.clientLabel ?? "—",
        description: entry.description ?? "—",
        paymentMethodLabel: this._formatPaymentMethod(entry.paymentMethod),
        amountExclVat: entry.amountExclVat ? this._formatAmount(entry.amountExclVat) + " €" : "—",
        vatAmount: vat === 0 ? "—" : this._formatAmount(entry.vatAmount) + " €",
        amountInclVat: this._formatAmount(entry.amountInclVat) + " €",
        sourceLabel,
      });

      const timeEl = fragment.querySelector('[data-slot-attr-datetime="entryDateIso"]');
      if (timeEl && entry.entryDate) timeEl.setAttribute("datetime", entry.entryDate);

      const pill = fragment.querySelector('[data-slot-element="sourcePill"]');
      const modifier = SOURCE_PILL_MODIFIER[sourceType];
      if (pill && modifier) pill.classList.add(modifier);

      const cellHt = fragment.querySelector('[data-slot-element="cellHt"]');
      const cellVat = fragment.querySelector('[data-slot-element="cellVat"]');
      if (cellHt) cellHt.hidden = !this._isVatSubject;
      if (cellVat) cellVat.hidden = !this._isVatSubject;

      this._renderPieceCell(fragment, entry, sourceType);

      this.tbodyTarget.appendChild(fragment);
    }
  }

  _renderPieceCell(fragment, entry, sourceType) {
    const cell = fragment.querySelector('[data-slot-element="pieceCell"]');
    if (!cell) return;
    this._clearChildren(cell);

    let href = null;
    let label = entry.pieceReference ?? "—";

    if (sourceType === "invoice") {
      if (entry.invoiceId) {
        href = "/facture-detail.html?id=" + encodeURIComponent(entry.invoiceId);
        label = entry.invoiceNumber || entry.pieceReference || "—";
      } else {
        label = entry.invoiceNumber || entry.pieceReference || "—";
      }
    } else if (sourceType === "cash_register_named" || sourceType === "cash_register_anonymous") {
      if (entry.cashRegisterSessionId) {
        href = "/caisse-session.html?id=" + encodeURIComponent(entry.cashRegisterSessionId);
        label = entry.pieceReference || "—";
      } else {
        label = entry.pieceReference || "—";
      }
    } else {
      label = entry.pieceReference || "—";
    }

    if (href) {
      const linkFragment = this.cloneTemplate("tpl-revenue-detail-piece-link");
      this.fillSlots(linkFragment, { pieceLabel: label });
      const anchor = linkFragment.querySelector("a");
      if (anchor) anchor.setAttribute("href", href);
      cell.appendChild(linkFragment);
    } else {
      const spanFragment = this.cloneTemplate("tpl-revenue-detail-piece-text");
      this.fillSlots(spanFragment, { pieceLabel: label });
      cell.appendChild(spanFragment);
    }
  }

  _renderTotals(totals) {
    if (this.hasTfootTarget) this.tfootTarget.hidden = false;
    if (this.hasTotalExclVatTarget) {
      this.totalExclVatTarget.textContent = this._formatAmount(totals?.totalExclVat) + " €";
    }
    if (this.hasTotalVatTarget) {
      this.totalVatTarget.textContent = totals?.totalVat === "0.00"
        ? "—"
        : this._formatAmount(totals?.totalVat) + " €";
    }
    if (this.hasTotalInclVatTarget) {
      this.totalInclVatTarget.textContent = this._formatAmount(totals?.totalInclVat) + " €";
    }
  }

  _renderBanners(meta) {
    const sealed = Boolean(meta?.sealed);
    const confirmed = Boolean(meta?.confirmed);
    const preConfirmWindow = Boolean(meta?.preConfirmWindow);
    const hashDiverged = Boolean(meta?.hashDiverged);
    const retroactiveCount = Number(meta?.retroactiveCount ?? 0);

    if (this.hasSealedBannerTarget) {
      this.sealedBannerTarget.hidden = !sealed;
      if (sealed && this.hasSealedAtTarget) {
        this.sealedAtTarget.textContent = this._formatDate(meta?.yearClosedAt);
        if (meta?.yearClosedAt) this.sealedAtTarget.setAttribute("datetime", meta.yearClosedAt);
      }
    }

    if (this.hasConfirmedBannerTarget) {
      const showConfirmedBanner = confirmed && !sealed;
      this.confirmedBannerTarget.hidden = !showConfirmedBanner;
      if (showConfirmedBanner && this.hasConfirmedAtTarget) {
        this.confirmedAtTarget.textContent = this._formatDate(meta?.confirmedAt);
        if (meta?.confirmedAt) this.confirmedAtTarget.setAttribute("datetime", meta.confirmedAt);
      }
    }

    if (this.hasPreConfirmBannerTarget) {
      const show = preConfirmWindow && !sealed && !confirmed;
      this.preConfirmBannerTarget.hidden = !show;
      if (show) {
        const monthLabel = this._capitalize(MONTH_LABELS[this._month - 1] ?? "");
        if (this.hasPreConfirmTitleTarget) {
          this.preConfirmTitleTarget.textContent = `${monthLabel} ${this._year} — en cours de consolidation.`;
        }
        if (this.hasPreConfirmDateTarget) {
          // La fenêtre §7.I.F ouvre le 8 du mois M+1.
          const next = new Date(this._year, this._month, 1);
          next.setDate(8);
          this.preConfirmDateTarget.textContent = next.toLocaleDateString("fr-FR", {
            day: "2-digit", month: "long", year: "numeric",
          });
        }
      }
    }

    if (this.hasHashIntegrityBannerTarget) {
      this.hashIntegrityBannerTarget.hidden = !hashDiverged;
    }

    if (this.hasRetroactiveBannerTarget) {
      this.retroactiveBannerTarget.hidden = retroactiveCount === 0 || sealed;
      if (retroactiveCount > 0 && this.hasRetroactiveTitleTarget) {
        this.retroactiveTitleTarget.textContent =
          retroactiveCount + " écriture" + (retroactiveCount > 1 ? "s" : "")
          + " rétroactive" + (retroactiveCount > 1 ? "s" : "");
      }
    }
  }

  _updateActionButtons(meta) {
    const sealed = Boolean(meta?.sealed);
    const confirmEligible = Boolean(meta?.confirmEligible);

    // Mois scellé (exercice clôturé §7.H.G) : plus aucune écriture possible.
    // On masque les CTA de saisie plutôt que de les désactiver — un bouton
    // grisé laissait penser qu'une action restait envisageable.
    for (const targetName of ["addPaymentButton", "openSessionButton"]) {
      const hasTarget = "has" + this._cap(targetName) + "Target";
      if (!this[hasTarget]) continue;
      this[targetName + "Target"].hidden = sealed;
    }

    if (this.hasConfirmMonthButtonTarget) {
      this.confirmMonthButtonTarget.hidden = !confirmEligible;
      this.confirmMonthButtonTarget.dataset.year = String(this._year);
      this.confirmMonthButtonTarget.dataset.month = String(this._month);
    }
  }

  _maybeAutoOpenConfirmModal(meta) {
    const params = new URLSearchParams(window.location.search);
    if (params.get("confirm") !== "1") return;
    if (!meta?.confirmEligible) return;
    if (!this.hasConfirmMonthButtonTarget) return;
    // Émule un click sur le CTA pour ouvrir la modale via le controller
    // `revenue-register-month-confirm` câblé au même bouton.
    this.confirmMonthButtonTarget.click();
    // Nettoie l'URL pour éviter une réouverture sur refresh.
    params.delete("confirm");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? "?" + qs : ""),
    );
  }

  _isFutureMonth() {
    const today = new Date();
    if (this._year > today.getFullYear()) return true;
    if (this._year < today.getFullYear()) return false;
    return this._month > (today.getMonth() + 1);
  }

  _clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  _capitalize(s) {
    if (!s || typeof s !== "string") return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  _cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  _formatAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "0,00";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    return num.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  _formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  }

  _formatPaymentMethod(method) {
    if (!method) return "—";
    const labels = {
      especes: "Espèces",
      cash: "Espèces",
      cb: "Carte bancaire",
      cheque: "Chèque",
      virement: "Virement",
      wero: "Wero",
      autre: "Autre",
    };
    return labels[method] ?? method;
  }
}
